import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Layout from './components/Layout.jsx';
import { ErrorBoundary, RouteError } from './components/ErrorBoundary.jsx';
import { PageReady } from './components/PageReady.jsx';
import { RouteFallback } from './components/RouteFallback.jsx';

// Home is rendered eagerly — it's the most likely entry point and we want
// the hero painted on first paint, not after a JS chunk roundtrip.
import Home from './components/pages/Home.jsx';

const Portfolio = lazy(() => import('./components/pages/Portfolio.jsx'));
const PortfolioDetail = lazy(() => import('./components/pages/PortfolioDetail.jsx'));
const Club = lazy(() => import('./components/pages/Club.jsx'));
const Brand = lazy(() => import('./components/pages/Brand.jsx'));
const Contact = lazy(() => import('./components/pages/Contact.jsx'));

// Every route element goes through route() or lazyRoute(): Layout starts a
// page's animations only once its PageReady reports the content, so a bare
// element would never animate and its .reveal blocks would never be revealed
// (they stay invisible unless the visitor prefers reduced motion).
const route = (Element) => (
  <PageReady>
    <Element />
  </PageReady>
);

const lazyRoute = (Element) => <Suspense fallback={<RouteFallback />}>{route(Element)}</Suspense>;

// Data router (vs BrowserRouter) is required for `<Link viewTransition>` to fire
// `document.startViewTransition` — this enables the Apple-style shared-element
// morph between Home cards and the destination page hero.
const router = createBrowserRouter([
  {
    element: <Layout />,
    // An error thrown while rendering a route (a page chunk that failed to
    // load, a page that threw) lands here and replaces Layout, nav included.
    errorElement: <RouteError />,
    children: [
      { path: '/', element: route(Home) },
      { path: '/portfolio', element: lazyRoute(Portfolio) },
      { path: '/portfolio/:slug', element: lazyRoute(PortfolioDetail) },
      { path: '/club', element: lazyRoute(Club) },
      { path: '/brand', element: lazyRoute(Brand) },
      { path: '/contact', element: lazyRoute(Contact) },
      { path: '*', element: route(Home) },
    ],
  },
]);

export default function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
